import PERMISSIONS from '@/constants/permissions';
import { FileModel } from '@/typings/api/files';
import { useHasAccess } from '@/utils/hooks';
import { F, getIntlTranslation } from '@/utils/intl';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { Button, Pagination, Tooltip, message } from 'antd';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'umi';
import FileDetailsDrawer from './FileDetailsDrawer';
import FileGrid from './FileGrid';
import FileLibraryToolbar from './FileLibraryToolbar';
import { useFileDeletion } from './useFileDeletion';
import { useFileLibrary } from './useFileLibrary';

import styles from './FileManager.less';

/**
 * Browse, upload, rename and delete the partner's files.
 *
 * The same body serves the file manager page and the Design touchpoint's Files
 * section: files belong to the partner rather than to a journey, so wherever
 * they are managed from, it is one library and the actions on it are identical.
 *
 * This is the only place a file can be deleted, which is why the "what would
 * break" check lives behind the button here ({@link useFileDeletion}).
 */
const FileManager: React.FC = () => {
  const intl = useIntl();
  const library = useFileLibrary();
  const canDelete = useHasAccess([PERMISSIONS.Files.DELETE]);
  // Renaming is the same right as uploading (FilestorePolicy::update), and an
  // email author holds it without files_upload.
  const canEditFile = useHasAccess([PERMISSIONS.Files.UPLOAD]);
  const canAuthorEmails = useHasAccess([PERMISSIONS.EmailTemplate.DRAFT]);
  const canEdit = canEditFile || canAuthorEmails;

  const [editing, setEditing] = useState<FileModel | null>(null);

  const { requestDelete, busyId, contextHolder } = useFileDeletion({
    loadUsage: library.usage,
    onConfirmed: (file, inUse) => library.remove(file, inUse),
  });

  const copyUrl = useCallback(
    (file: FileModel) => {
      void navigator.clipboard?.writeText(file.url);
      message.success(
        getIntlTranslation(intl, {
          id: 'pages.files.urlCopied',
          defaultMessage: 'Link copied.',
        }),
      );
    },
    [intl],
  );

  return (
    <>
      {/* The delete dialog lives here, inside the providers it reads from. */}
      {contextHolder}

      <FileLibraryToolbar
        search={library.filters.search}
        onSearch={(search) => library.applyFilters({ search })}
        kind={library.filters.kind}
        onKindChange={(kind) => library.applyFilters({ kind })}
        uploading={library.uploading}
        onUpload={(file) => void library.upload(file)}
      />

      <FileGrid
        files={library.items}
        loading={library.loading}
        renderActions={(file) => (
          <>
            <Tooltip
              title={
                <F id="pages.files.actions.copy" defaultMessage="Copy link" />
              }
            >
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copyUrl(file)}
              />
            </Tooltip>

            <Tooltip
              title={<F id="pages.files.actions.open" defaultMessage="Open" />}
            >
              <Button
                size="small"
                type="text"
                icon={<ExportOutlined />}
                href={file.url}
                target="_blank"
                rel="noreferrer"
              />
            </Tooltip>

            {canEdit && (
              <Tooltip
                title={
                  <F id="common.actions.rename" defaultMessage="Rename" />
                }
              >
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => setEditing(file)}
                />
              </Tooltip>
            )}

            {canDelete && (
              <Tooltip
                title={<F id="common.actions.delete" defaultMessage="Delete" />}
              >
                <Button
                  size="small"
                  type="text"
                  danger
                  loading={busyId === file.id}
                  icon={<DeleteOutlined />}
                  onClick={() => void requestDelete(file)}
                />
              </Tooltip>
            )}
          </>
        )}
      />

      {library.total > library.pageSize && (
        <Pagination
          className={styles.pagination}
          current={library.page}
          pageSize={library.pageSize}
          total={library.total}
          showSizeChanger={false}
          onChange={library.setPage}
        />
      )}

      <FileDetailsDrawer
        file={editing}
        onClose={() => setEditing(null)}
        onSave={async (name, description) => {
          if (editing) await library.rename(editing, name, description);
          setEditing(null);
        }}
      />
    </>
  );
};

export default FileManager;
